import * as THREE from 'three';
import { EventDispatcher } from '../EventDispatcher';
import { PolygonClipVolume } from './PolygonClipVolume';

// Declare jQuery as global (used by legacy Potree UI code)
declare const $: (
  template: TemplateStringsArray | string,
  ...args: unknown[]
) => {
  find(selector: string): { each(fn: (index: number, target: SVGPolylineElement) => void): void };
  remove(): void;
  0: SVGSVGElement;
  [Symbol.iterator](): Iterator<SVGSVGElement>;
};

/** Minimal Potree viewer interface used by ClippingTool */
interface PotreeViewer {
  renderer: THREE.WebGLRenderer;
  scene: {
    getActiveCamera(): THREE.Camera;
    addPolygonClipVolume(vol: PolygonClipVolume): void;
    removePolygonClipVolume(vol: PolygonClipVolume): void;
  };
  inputHandler: {
    registerInteractiveScene(scene: THREE.Scene): void;
    startDragging(obj: THREE.Object3D): void;
    addEventListener(type: string, listener: (e: PotreeEvent) => void): void;
    enabled: boolean;
  };
  dispatchEvent(event: { type: string; [key: string]: unknown }): void;
  addEventListener(type: string, listener: (e?: PotreeEvent) => void): void;
  removeEventListener(type: string, listener: (e?: PotreeEvent) => void): void;
}

/** Generic Potree event */
interface PotreeEvent {
  type: string;
  selection?: (PolygonClipVolume | THREE.Object3D)[];
  button?: number;
  volume?: THREE.Object3D;
  [key: string]: unknown;
}

/** Scene with Potree event methods */
interface PotreeScene {
  addEventListener(type: string, listener: (e: PotreeEvent) => void): void;
  removeEventListeners(type: string, listener?: (e: PotreeEvent) => void): void;
}

/** Arguments for startInsertion */
interface InsertionArgs {
  type?: string | null;
}

export class ClippingTool extends EventDispatcher {
  viewer: PotreeViewer;
  maxPolygonVertices: number;
  sceneMarker: THREE.Scene;
  sceneVolume: THREE.Scene;
  scene: PotreeScene | null = null;
  onRemove: (e: PotreeEvent) => void;
  onAdd: (e: PotreeEvent) => void;

  constructor(viewer: PotreeViewer) {
    super();

    this.viewer = viewer;

    this.maxPolygonVertices = 8;

    this.addEventListener('start_inserting_clipping_volume', () => {
      this.viewer.dispatchEvent({
        type: 'cancel_insertions',
      });
    });

    this.sceneMarker = new THREE.Scene();
    this.sceneVolume = new THREE.Scene();
    this.sceneVolume.name = 'scene_clip_volume';
    this.viewer.inputHandler.registerInteractiveScene(this.sceneVolume);

    this.onRemove = (e: PotreeEvent) => {
      if (e.volume) {
        this.sceneVolume.remove(e.volume);
      }
    };

    this.onAdd = (e: PotreeEvent) => {
      if (e.volume) {
        this.sceneVolume.add(e.volume);
      }
    };

    this.viewer.inputHandler.addEventListener('delete', (e: PotreeEvent) => {
      const selection = e.selection || [];
      const polyVolumes = selection.filter((item): item is PolygonClipVolume => item instanceof PolygonClipVolume);
      polyVolumes.forEach((vol) => this.viewer.scene.removePolygonClipVolume(vol));
    });
  }

  setScene(scene: PotreeScene) {
    if (this.scene === scene) {
      return;
    }

    if (this.scene) {
      this.scene.removeEventListeners('clip_volume_added', this.onAdd);
      this.scene.removeEventListeners('clip_volume_removed', this.onRemove);
      this.scene.removeEventListeners('polygon_clip_volume_added', this.onAdd);
      this.scene.removeEventListeners('polygon_clip_volume_removed', this.onRemove);
    }

    this.scene = scene;

    this.scene.addEventListener('clip_volume_added', this.onAdd);
    this.scene.addEventListener('clip_volume_removed', this.onRemove);
    this.scene.addEventListener('polygon_clip_volume_added', this.onAdd);
    this.scene.addEventListener('polygon_clip_volume_removed', this.onRemove);
  }

  startInsertion(args: InsertionArgs = {}) {
    const type = args.type || null;

    if (!type) return null;

    const domElement = this.viewer.renderer.domElement;
    const canvasSize = this.viewer.renderer.getSize(new THREE.Vector2());

    const svg = $(`
		<svg height="${canvasSize.height}" width="${canvasSize.width}" style="position:absolute; pointer-events: none">

			<defs>
				 <marker id="diamond" markerWidth="24" markerHeight="24" refX="12" refY="12"
						markerUnits="userSpaceOnUse">
					<circle cx="12" cy="12" r="6" fill="white" stroke="black" stroke-width="3"/>
				</marker>
			</defs>

			<polyline fill="none" stroke="black"
				style="stroke:rgb(0, 0, 0);
				stroke-width:6;"
				stroke-dasharray="9, 6"
				stroke-dashoffset="2"
				/>

			<polyline fill="none" stroke="black"
				style="stroke:rgb(255, 255, 255);
				stroke-width:2;"
				stroke-dasharray="5, 10"
				marker-start="url(#diamond)"
				marker-mid="url(#diamond)"
				marker-end="url(#diamond)"
				/>
		</svg>`);
    // @ts-expect-error - jQuery append on DOM parent element
    $(domElement.parentElement).append(svg);

    const polyClipVol = new PolygonClipVolume(this.viewer.scene.getActiveCamera().clone());

    this.dispatchEvent({ type: 'start_inserting_clipping_volume' });

    this.viewer.scene.addPolygonClipVolume(polyClipVol);
    this.sceneMarker.add(polyClipVol);

    const cancel: {
      callback: ((e?: MouseEvent | PotreeEvent) => void) | null;
    } = {
      callback: null,
    };

    const insertionCallback = (e: MouseEvent) => {
      if (e.button === THREE.MOUSE.LEFT) {
        polyClipVol.addMarker();

        // SVC Screen Line
        svg.find('polyline').each((_index: number, target: SVGPolylineElement) => {
          const newPoint = svg[0].createSVGPoint();
          newPoint.x = e.offsetX;
          newPoint.y = e.offsetY;
          target.points.appendItem(newPoint);
        });

        if (polyClipVol.markers.length > this.maxPolygonVertices) {
          cancel.callback?.();
        }

        this.viewer.inputHandler.startDragging(polyClipVol.markers[polyClipVol.markers.length - 1]);
      } else if (e.button === THREE.MOUSE.RIGHT) {
        cancel.callback?.(e);
      }
    };

    cancel.callback = () => {
      svg.remove();

      if (polyClipVol.markers.length > 3) {
        polyClipVol.removeLastMarker();
        polyClipVol.initialized = true;
      } else {
        this.viewer.scene.removePolygonClipVolume(polyClipVol);
      }

      this.viewer.renderer.domElement.removeEventListener('mouseup', insertionCallback, true);
      this.viewer.removeEventListener('cancel_insertions', cancel.callback as () => void);
      this.viewer.inputHandler.enabled = true;
    };

    this.viewer.addEventListener('cancel_insertions', cancel.callback as () => void);
    this.viewer.renderer.domElement.addEventListener('mouseup', insertionCallback, true);
    this.viewer.inputHandler.enabled = false;

    polyClipVol.addMarker();
    this.viewer.inputHandler.startDragging(polyClipVol.markers[polyClipVol.markers.length - 1]);

    return polyClipVol;
  }

  update() {
    // noop
  }
}
